import React from "react";
import { FileText, Loader2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * Miniatura della COPERTINA (pag. 1) di un asset PDF — componente condiviso.
 *
 * Usata in AdminAssets.jsx (lista file fornitore lato admin) e Suppliers.jsx
 * (lista file fornitore lato studio/trader).
 *
 * Caratteristiche:
 * - Lazy-load via IntersectionObserver: la richiesta `/api/assets/{id}/thumbnail`
 *   parte SOLO quando l'elemento entra nel viewport. Niente impatto sull'avvio.
 * - Hover (o focus tastiera) → popup ingrandito 280×395, render hi-res
 *   (width=480) caricato lazy al primo hover.
 * - Click → invoca `onClick` (tipicamente: apri viewer PDF.js interno).
 * - Cleanup automatico dei blob URL su unmount → niente memory leak.
 * - Popup positioning anti-overflow: a destra se c'è spazio, altrimenti a
 *   sinistra; verticalmente clamped al viewport.
 *
 * Performance:
 * - Miniatura piccola ≈ 10–15 KB. Cacheata server-side (PyMuPDF) + browser HTTP.
 * - Hi-res popup ≈ 30 KB, caricato 1 sola volta per asset, on-demand.
 *
 * Props:
 *   - assetId (string, required): ID dell'asset PDF
 *   - testid (string, optional): valore data-testid del bottone (e il popup
 *     riceve `${testid}-zoom`)
 *   - onClick (function, optional): callback al click sulla miniatura
 *   - title (string, optional): tooltip native sull'hover
 *   - size ("sm"|"md", optional, default "sm"): preset dimensioni miniatura.
 *     "sm" = 40×56 (compatta per liste fitte come AdminAssets)
 *     "md" = 56×80 (più visibile per Suppliers studio/trader)
 */
export function AssetCoverThumbnail({ assetId, testid, onClick, title, size = "sm" }) {
  const [src, setSrc] = React.useState("");
  const [error, setError] = React.useState(false);
  const [hover, setHover] = React.useState(false);
  const [zoomSrc, setZoomSrc] = React.useState("");
  const [zoomLoading, setZoomLoading] = React.useState(false);
  const [zoomPos, setZoomPos] = React.useState({ top: 0, left: 0 });
  const ref = React.useRef(null);
  const loadedRef = React.useRef(false);
  const zoomLoadedRef = React.useRef(false);

  React.useEffect(() => {
    if (!assetId) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(async (entries) => {
      if (loadedRef.current) return;
      if (!entries.some((e) => e.isIntersecting)) return;
      loadedRef.current = true;
      try {
        const { data } = await api.get(`/assets/${assetId}/thumbnail`, {
          params: { page: 1, width: 200 },
          responseType: "blob",
        });
        const url = URL.createObjectURL(data);
        setSrc(url);
      } catch (_) {
        setError(true);
      }
    }, { rootMargin: "200px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [assetId]);

  const loadZoom = React.useCallback(async () => {
    if (zoomLoadedRef.current || !assetId) return;
    zoomLoadedRef.current = true;
    setZoomLoading(true);
    try {
      const { data } = await api.get(`/assets/${assetId}/thumbnail`, {
        params: { page: 1, width: 480 },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      setZoomSrc(url);
    } catch (_) {
      // fallback: il popup mostra il thumbnail piccolo se il hi-res fallisce
    } finally {
      setZoomLoading(false);
    }
  }, [assetId]);

  React.useEffect(() => () => {
    if (src) URL.revokeObjectURL(src);
    if (zoomSrc) URL.revokeObjectURL(zoomSrc);
  }, [src, zoomSrc]);

  const handleEnter = (e) => {
    setHover(true);
    loadZoom();
    const rect = e.currentTarget.getBoundingClientRect();
    const popupW = 280;
    const margin = 12;
    const viewportW = window.innerWidth;
    let left = rect.right + margin;
    if (left + popupW > viewportW - 8) left = rect.left - margin - popupW;
    setZoomPos({ top: rect.top, left });
  };
  const handleLeave = () => setHover(false);

  // Dimensioni miniatura in base al preset
  const dim = size === "md" ? "w-14 h-20" : "w-10 h-14";
  const iconSize = size === "md" ? 16 : 14;
  const spinnerSize = size === "md" ? 14 : 12;
  const baseClass = `shrink-0 ${dim} bg-cream2/60 border border-ink/15 rounded-sm overflow-hidden relative flex items-center justify-center cursor-pointer hover:border-gold hover:shadow-sm transition-all`;

  const zoomPopup = hover ? (
    <div
      className="fixed z-[9999] pointer-events-none animate-in fade-in duration-150"
      style={{
        top: Math.max(8, Math.min(zoomPos.top, window.innerHeight - 412)),
        left: zoomPos.left,
        width: 280,
      }}
      data-testid={testid ? `${testid}-zoom` : undefined}
    >
      <div className="bg-white border border-ink/20 rounded-sm shadow-2xl overflow-hidden">
        <div className="w-[280px] h-[395px] bg-cream2 flex items-center justify-center">
          {zoomLoading && !zoomSrc && (
            <Loader2 size={20} className="text-slate1/40 animate-spin" />
          )}
          {(zoomSrc || src) && (
            <img
              src={zoomSrc || src}
              alt="Anteprima copertina"
              className="w-full h-full object-contain"
              draggable={false}
            />
          )}
        </div>
      </div>
    </div>
  ) : null;

  const inner = error ? (
    <FileText size={iconSize} className="text-rose-300" />
  ) : !src ? (
    <Loader2 size={spinnerSize} className="text-slate1/40 animate-spin" />
  ) : (
    <img src={src} alt="Copertina" loading="lazy" className="w-full h-full object-cover" />
  );

  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={(e) => { e.stopPropagation(); if (onClick) onClick(); }}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
        className={baseClass}
        data-testid={testid}
        title={title || "Apri anteprima"}
      >
        {inner}
      </button>
      {zoomPopup}
    </>
  );
}

export default AssetCoverThumbnail;
